<template>
    <RecollectionStatus v-if="request" :request="request" />

    <div v-else-if="options.length" class="recollection-offer text-start">
        <h6 class="fw-semibold mb-1">We need a new sample</h6>
        <p class="mb-3">
            We couldn't get a reliable result from your sample. We can send you a new sample kit free of
            charge<template v-if="refund">, or refund what was paid for the kit, less
                {{ refund.refundDeductionPercent }}% for handling and shipping</template>.
        </p>
        <router-link :to="{ name: 'new-sample-kit', params: { barcode } }" class="btn btn-primary">
            {{ refund ? "Choose a new kit or a refund" : "Request a new sample kit" }}
        </router-link>
    </div>

    <template v-else-if="loaded">
        <p v-if="noMoreTests" class="text-start mb-2">
            We're sorry: after three tests, we can't send another sample kit or offer a refund.
        </p>
        <!-- Nothing to ask for (or the request couldn't be loaded): whatever the page said before. -->
        <slot />
    </template>
</template>

<script setup>
import { computed, ref, watch, defineProps } from "vue";
import { getRecollection, recollectionOptions } from "@/services/my-tests";
import RecollectionStatus from "@/components/RecollectionStatus.vue";

const props = defineProps({
    barcode: String,
});

const loaded = ref(false);
const recollection = ref(null);

const request = computed(() => recollection.value?.request ?? null);
const options = computed(() => recollectionOptions(recollection.value));
const refund = computed(() => options.value.find((option) => option.choice === "refund") ?? null);
const noMoreTests = computed(() => !request.value && !options.value.length && recollection.value?.attempt >= 3);

const load = async () => {
    loaded.value = false;
    recollection.value = null;
    if (props.barcode) {
        try {
            recollection.value = await getRecollection(props.barcode);
        } catch (e) {
            // Leave the page as it was.
        }
    }
    loaded.value = true;
};

watch(() => props.barcode, load, { immediate: true });
</script>

<style lang="scss" scoped>
.recollection-offer {
    border-radius: 0.75rem;
    padding: 1rem 1.25rem;
    background: var(--bs-danger-bg-subtle, #f8d7da);
    border-left: 4px solid var(--bs-danger, #dc3545);
}
</style>
